import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const StudentDashboard = () => {
  const [studentData, setStudentData] = useState(null);
  const [skills, setSkills] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      const usn = localStorage.getItem('usn');
      
      console.log('Fetching data for USN:', usn);
      
      if (!usn) {
        alert('Please login first');
        navigate('/student-view-login');
        return;
      }

      // ✅ CHANGED: Fetch student by USN instead of by ID
      const [studentRes, skillsRes, interviewsRes, placementsRes, internshipsRes] = await Promise.all([
        api.get(`/students/usn/${usn}`), // ✅ CHANGED: Fetch by USN
        api.get(`/skills/student/${usn}`),
        api.get(`/interviews/student/${usn}`),
        api.get(`/placements/student/${usn}`),
        api.get(`/internships/student/${usn}`)
      ]);

      console.log('Student:', studentRes.data);
      console.log('Skills:', skillsRes.data);
      console.log('Interviews:', interviewsRes.data);
      console.log('Placements:', placementsRes.data);
      console.log('Internships:', internshipsRes.data);

      setStudentData(studentRes.data);
      setSkills(skillsRes.data);
      setInterviews(interviewsRes.data);
      setPlacements(placementsRes.data);
      setInternships(internshipsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error loading dashboard data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>👁️ Student Dashboard</h1>
          <p style={styles.headerSubtitle}>Your Placement Records</p>
        </div>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate('/tables')} style={styles.editButton}>
            ✏️ Edit Data
          </button>
          <button onClick={handleLogout} style={styles.logoutButton}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Student Info Card */}
      {studentData && (
        <div style={styles.infoCard}>
          <div style={styles.profileIcon}>👨‍🎓</div>
          <div style={styles.profileInfo}>
            <h2 style={styles.profileName}>{studentData.name}</h2>
            <div style={styles.profileDetails}>
              <span>📧 {studentData.email}</span>
              <span>📱 {studentData.phone}</span>
              <span>🎓 {studentData.branch}</span>
              <span>📊 CGPA: {studentData.cgpa}</span>
              <span>🎯 USN: {studentData.usn}</span>
              <span>📅 Graduation: {studentData.graduationYear}</span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💼</div>
          <div style={styles.statNumber}>{placements.length}</div>
          <div style={styles.statLabel}>Placements</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>🎯</div>
          <div style={styles.statNumber}>{interviews.length}</div>
          <div style={styles.statLabel}>Interviews</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>⚡</div>
          <div style={styles.statNumber}>{skills.length}</div>
          <div style={styles.statLabel}>Skills</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>📝</div>
          <div style={styles.statNumber}>{internships.length}</div>
          <div style={styles.statLabel}>Internships</div>
        </div>
      </div>

      {/* Skills Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🎯 My Skills</h3>
        <div style={styles.skillsGrid}>
          {skills.length > 0 ? (
            skills.map((skill, index) => (
              <div key={index} style={styles.skillCard}>
                <div style={styles.skillName}>{skill.skillName}</div>
                <div style={styles.skillBadge}>{skill.proficiency}</div>
              </div>
            ))
          ) : (
            <p style={styles.emptyMessage}>No skills added yet</p>
          )}
        </div>
      </div>

      {/* Placements Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>💼 My Placements</h3>
        {placements.length > 0 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Job Role</th>
                  <th style={styles.th}>Salary (LPA)</th>
                  <th style={styles.th}>Joining Date</th>
                </tr>
              </thead>
              <tbody>
                {placements.map((placement, index) => (
                  <tr key={index}>
                    <td style={styles.td}>{placement.companyName}</td>
                    <td style={styles.td}>{placement.jobRole}</td>
                    <td style={styles.td}>{placement.salary}</td>
                    <td style={styles.td}>{new Date(placement.joiningDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={styles.emptyMessage}>No placements added yet</p>
        )}
      </div>

      {/* Interviews Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🎯 My Interviews</h3>
        {interviews.length > 0 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Mode</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Result</th>
                </tr>
              </thead>
              <tbody>
                {interviews.map((interview, index) => (
                  <tr key={index}>
                    <td style={styles.td}>{interview.companyName}</td>
                    <td style={styles.td}>{interview.mode}</td>
                    <td style={styles.td}>{new Date(interview.date).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        background: interview.result === 'Selected' ? '#48bb78' :
                                   interview.result === 'Rejected' ? '#f56565' :
                                   interview.result === 'Pending' ? '#ed8936' : '#4299e1'
                      }}>
                        {interview.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={styles.emptyMessage}>No interviews scheduled yet</p>
        )}
      </div>

      {/* Internships Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>📝 My Internships</h3>
        {internships.length > 0 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Duration (months)</th>
                  <th style={styles.th}>Stipend</th>
                  <th style={styles.th}>Start Date</th>
                </tr>
              </thead>
              <tbody>
                {internships.map((internship, index) => (
                  <tr key={index}>
                    <td style={styles.td}>{internship.companyName}</td>
                    <td style={styles.td}>{internship.role}</td>
                    <td style={styles.td}>{internship.duration}</td>
                    <td style={styles.td}>₹{internship.stipend}</td>
                    <td style={styles.td}>{new Date(internship.startDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={styles.emptyMessage}>No internships added yet</p>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  loader: {
    fontSize: '2rem',
    color: 'white',
    fontWeight: '600',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.95)',
    padding: '2rem',
    borderRadius: '15px',
    marginBottom: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  headerTitle: {
    margin: 0,
    color: '#333',
    fontSize: '2rem',
  },
  headerSubtitle: {
    margin: '0.5rem 0 0 0',
    color: '#666',
  },
  headerButtons: {
    display: 'flex',
    gap: '1rem',
  },
  editButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '0.8rem 1.5rem',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  logoutButton: {
    background: '#f56565',
    color: 'white',
    border: 'none',
    padding: '0.8rem 1.5rem',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  infoCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  profileIcon: {
    fontSize: '80px',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    margin: '0 0 1rem 0',
    fontSize: '2rem',
    color: '#333',
  },
  profileDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.8rem',
    color: '#666',
    fontSize: '1rem',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  statCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  statIcon: {
    fontSize: '50px',
    marginBottom: '1rem',
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '0.5rem',
  },
  statLabel: {
    color: '#666',
    fontSize: '1.1rem',
  },
  section: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  sectionTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.8rem',
    color: '#333',
  },
  skillsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
  },
  skillCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '1.5rem',
    borderRadius: '10px',
    textAlign: 'center',
  },
  skillName: {
    fontSize: '1.2rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  skillBadge: {
    background: 'rgba(255,255,255,0.3)',
    padding: '0.3rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.9rem',
    display: 'inline-block',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: '#f7fafc',
    padding: '1rem',
    textAlign: 'left',
    fontWeight: '600',
    color: '#333',
    borderBottom: '2px solid #e2e8f0',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    color: '#666',
  },
  statusBadge: {
    padding: '0.3rem 0.8rem',
    borderRadius: '20px',
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    fontSize: '1.1rem',
    padding: '2rem',
  },
};

export default StudentDashboard;
